import React, { useState, useCallback, useEffect } from 'react';
import {
    TableRow, TableCell, IconButton, Collapse, Box, Typography, Table, TableBody,
    TableHead, TextField, Button, Autocomplete, Grid, Chip, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, Paper, Checkbox, FormControlLabel, CircularProgress
} from '@mui/material';
import {
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { usePartyAutocomplete, useProductAutocomplete } from '../hooks/useAutocomplete';

const STORAGE_LOCATIONS = ['Mill', 'Godown', 'Nora'];

const OrderRow = ({ order, userRole, userLocation, onOrderUpdate, showOnlyOrdered = true }) => {
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedOrder, setEditedOrder] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, message: '' });
    const [errors, setErrors] = useState({});
    const [originalOrder, setOriginalOrder] = useState(null);
    const [selectedProductsToShip, setSelectedProductsToShip] = useState([]);
    const [selectedProductsToUnship, setSelectedProductsToUnship] = useState([]);
    const [selectedProductsToBill, setSelectedProductsToBill] = useState([]);
    const [selectedProductsToUnbill, setSelectedProductsToUnbill] = useState([]);
    const [stockInfo, setStockInfo] = useState({}); // Store stock info for each product by index

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const canEditOrder = userRole === 'owner' || (userRole === 'employee' && userLocation === 'Universal');
    const canEditProduct = (product) => {
        if (userRole === 'owner' || userLocation === 'Universal') return true;
        return product.location === userLocation;
    };
    const canEditParty = userRole === 'owner' || userLocation === 'Universal';

    // Autocomplete hooks
    const {
        options: partyOptions,
        loading: partyLoading,
        fetchSuggestions: fetchParties
    } = usePartyAutocomplete({ debounceMs: 300, minLength: 2 });

    const {
        options: productOptions,
        loading: productLoading,
        fetchSuggestions: fetchProducts
    } = useProductAutocomplete({ debounceMs: 300, minLength: 2 });

    // Filter products to show in collapsed view (only "ordered" status)
    const displayedProducts = showOnlyOrdered
        ? order.products.filter(p => p.status === 'ordered')
        : order.products;

    const fetchExpandedData = useCallback(async () => {
        try {
            // Only fetch order details (parties and products now use autocomplete)
            const orderResponse = await api.get(`/orders/${order._id}`);
            setOriginalOrder(orderResponse.data);
            setEditedOrder(orderResponse.data);
        } catch (error) {
            console.error('Error fetching order details:', error);
            setSnackbar({ open: true, message: 'Failed to fetch order details', severity: 'error' });
        }
    }, [order._id]);

    const handleRowClick = async () => {
        if (!open) {
            await fetchExpandedData();
        }
        setOpen(!open);
    };

    const handleEditToggle = () => {
        if (isEditing) {
            setEditedOrder(JSON.parse(JSON.stringify(originalOrder)));
            setErrors({});
            setStockInfo({}); // Clear stock info when exiting edit mode
        }
        setIsEditing(!isEditing);
    };

    // Batch fetch stock info for all products at once (reduces API calls)
    const fetchBatchStockInfo = useCallback(async () => {
        try {
            // Filter out shipped products and build request payload
            const productsToFetch = editedOrder.products
                .map((product, index) => ({
                    productName: product.productName,
                    location: product.location,
                    index // Keep track of original index
                }))
                .filter(p => p.productName && p.location && editedOrder.products[p.index].status !== 'shipped');

            if (productsToFetch.length === 0) return;

            // Call batch stock info endpoint
            const response = await api.post('/products/stock-info/batch', {
                products: productsToFetch.map(p => ({
                    productName: p.productName,
                    location: p.location
                })),
                excludeOrderId: order._id
            });

            // Map results back to stockInfo state by index
            const newStockInfo = {};
            response.data.results.forEach((result, i) => {
                const originalIndex = productsToFetch[i].index;
                newStockInfo[originalIndex] = {
                    location: result.location,
                    current: result.current,
                    reserved: result.reserved,
                    available: result.available
                };
            });

            setStockInfo(newStockInfo);
        } catch (error) {
            console.error('Error fetching batch stock info:', error);
            setSnackbar({ open: true, message: 'Failed to fetch stock information', severity: 'error' });
        }
    }, [editedOrder, order._id]);

    // Fetch stock info for all products when entering edit mode using BATCH endpoint
    // Only fetch when isEditing becomes true, not on every editedOrder change
    useEffect(() => {
        if (isEditing && editedOrder) {
            fetchBatchStockInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    // Fetch stock info for a single product (used when adding NEW products during editing)
    const fetchStockInfoForLocation = async (productName, location, index) => {
        try {
            const response = await api.get(`/products/stock-info?productName=${encodeURIComponent(productName)}&excludeOrderId=${order._id}`);
            const locationInfo = response.data.locations.find(loc => loc.location === location);

            setStockInfo(prev => ({
                ...prev,
                [index]: locationInfo || { location, current: 0, reserved: 0, available: 0 }
            }));
        } catch (error) {
            console.error('Error fetching stock info:', error);
            setStockInfo(prev => ({
                ...prev,
                [index]: null
            }));
        }
    };

    const handlePartyChange = (event, newValue) => {
        if (!canEditParty) return;

        setEditedOrder({
            ...editedOrder,
            party: newValue || null,
            partyId: newValue?._id || null
        });
        setErrors({ ...errors, party: null });
    };

    const handleProductChange = async (index, field, value, event = null) => {
        const updatedProducts = [...editedOrder.products];
        if (updatedProducts[index].status === 'shipped') return;

        if (field === 'product') {
            // value is the selected product object from Autocomplete
            if (value) {
                const previousLocation = updatedProducts[index].location;
                updatedProducts[index] = {
                    ...value,
                    quantity: updatedProducts[index].quantity,
                    status: updatedProducts[index].status,
                    location: previousLocation,
                    rateIncludesGST: value.rateIncludesGST || false
                };
                await fetchStockInfoForLocation(value.productName, previousLocation, index);
            } else {
                updatedProducts[index].productName = '';
            }
            setEditedOrder({ ...editedOrder, products: updatedProducts });
            setErrors(prev => ({ ...prev, [`productName-${index}`]: '' }));
            return;
        }

        if (field === 'location') {
            if (STORAGE_LOCATIONS.includes(value)) {
                updatedProducts[index].location = value;
                updatedProducts[index].locationChanged = true; // Mark for validation

                // Fetch stock info for new location
                if (updatedProducts[index].productName) {
                    await fetchStockInfoForLocation(updatedProducts[index].productName, value, index);
                }
            } else {
                return;
            }
        } else if (field === 'product' && value) {
            const previousLocation = updatedProducts[index].location;
            updatedProducts[index] = {
                ...value,
                quantity: updatedProducts[index].quantity,
                status: updatedProducts[index].status,
                location: previousLocation, // Keep previous location when product changes
                rateIncludesGST: value.rateIncludesGST || false // Get GST setting from product
            };

            // Fetch stock info for the new product at current location
            await fetchStockInfoForLocation(value.productName, previousLocation, index);
        } else if (field === 'quantity') {
            updatedProducts[index][field] = value;

            // Validate quantity against stock if we have stock info
            if (stockInfo[index] && value) {
                const quantity = parseFloat(value);
                const currentStock = stockInfo[index].current;
                const reservedStock = stockInfo[index].reserved;
                const availableStock = stockInfo[index].available;

                if (quantity > currentStock) {
                    setErrors(prev => ({
                        ...prev,
                        [`quantity-${index}`]: `Quantity exceeds current stock (${currentStock}) at ${updatedProducts[index].location}`
                    }));
                } else if (quantity > availableStock) {
                    setErrors(prev => ({
                        ...prev,
                        [`quantity-${index}`]: `Warning: Quantity exceeds available stock (${availableStock}). Reserved: ${reservedStock}`
                    }));
                } else {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`quantity-${index}`];
                        return newErrors;
                    });
                }
            }
        } else if (field !== 'product') {
            updatedProducts[index][field] = value;
        }
        setEditedOrder({ ...editedOrder, products: updatedProducts });
        if (field !== 'quantity') {
            setErrors(prev => ({ ...prev, [`${field}-${index}`]: '' }));
        }
    };

    const handleAddProduct = () => {
        const newProduct = {
            productName: '',
            quantity: 0,
            rate: 0,
            rateIncludesGST: false,
            status: 'ordered',
            location: userLocation === 'Universal' ? 'Mill' : userLocation
        };
        setEditedOrder({ ...editedOrder, products: [...editedOrder.products, newProduct] });
    };

    const handleRemoveProduct = (index) => {
        if (editedOrder.products[index].status === 'shipped') {
            setSnackbar({ open: true, message: 'Cannot remove shipped products', severity: 'warning' });
            return;
        }

        setConfirmDialog({
            open: true,
            message: 'Are you sure you want to remove this product?',
            action: () => {
                const updatedProducts = editedOrder.products.filter((_, i) => i !== index);
                setEditedOrder({ ...editedOrder, products: updatedProducts });
                setConfirmDialog({ open: false, action: null, message: '' });

                if (updatedProducts.length === 0) {
                    setSnackbar({
                        open: true,
                        message: 'All products removed. Order will be deleted upon saving.',
                        severity: 'warning'
                    });
                }
            }
        });
    };

    const validateForm = async () => {
        const newErrors = {};

        // Check for duplicate product+location combinations
        const productLocationMap = new Map();
        for (let index = 0; index < editedOrder.products.length; index++) {
            const product = editedOrder.products[index];
            const key = `${product.productName}-${product.location}`;

            if (productLocationMap.has(key)) {
                const firstIndex = productLocationMap.get(key);
                newErrors[`productName-${index}`] = `Duplicate: Same product at same location (first entry at row ${firstIndex + 1})`;
                newErrors[`productName-${firstIndex}`] = `Duplicate: Same product at same location (duplicate at row ${index + 1})`;
            } else {
                productLocationMap.set(key, index);
            }
        }

        // Validate all products and check stock
        for (let index = 0; index < editedOrder.products.length; index++) {
            const product = editedOrder.products[index];
            const originalProduct = originalOrder.products[index];

            if (!product.productName) newErrors[`productName-${index}`] = 'Product name is required';
            if (!product.quantity || product.quantity <= 0) newErrors[`quantity-${index}`] = 'Quantity must be greater than 0';
            if (!product.rate || product.rate <= 0) newErrors[`rate-${index}`] = 'Rate must be greater than 0';
            if (!product.location) newErrors[`location-${index}`] = 'Location is required';

            // Check stock if location OR quantity changed
            const locationChanged = originalProduct && product.location !== originalProduct.location;
            const quantityChanged = originalProduct && Number(product.quantity) !== Number(originalProduct.quantity);

            if (originalProduct && (locationChanged || quantityChanged)) {
                try {
                    const productData = await api.get(`/products?name=${product.productName}`);
                    const foundProduct = productData.data.find(p => p.productName === product.productName);

                    if (foundProduct) {
                        const locationStock = foundProduct.locationStock.find(ls => ls.location === product.location);
                        const availableStock = locationStock ? locationStock.stock : 0;

                        // Calculate actual available stock
                        // If location hasn't changed, add back the original quantity
                        const actualAvailable = locationChanged
                            ? availableStock
                            : availableStock + Number(originalProduct.quantity);

                        if (actualAvailable < Number(product.quantity)) {
                            const errorField = locationChanged ? `location-${index}` : `quantity-${index}`;
                            newErrors[errorField] = `Insufficient stock at ${product.location}. Available: ${actualAvailable}, Required: ${product.quantity}`;
                        }
                    }
                } catch (error) {
                    console.error('Error checking stock:', error);
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveChanges = async () => {
        const isValid = await validateForm();
        if (!isValid) {
            setSnackbar({ open: true, message: 'Please fix all errors before saving', severity: 'error' });
            return;
        }

        try {
            if (editedOrder.products.length === 0) {
                await handleDeleteOrder();
                return;
            }

            const updatedOrder = {
                ...editedOrder,
                partyId: editedOrder.party._id || editedOrder.partyId,
                products: editedOrder.products.map(p => ({
                    ...p,
                    quantity: Number(p.quantity),
                    rate: Number(p.rate),
                    status: p.status,
                    location: p.location || 'Mill'
                }))
            };

            const response = await api.put(`/orders/${order._id}`, updatedOrder);
            setEditedOrder(response.data);
            setOriginalOrder(response.data);
            setIsEditing(false);
            onOrderUpdate();
            setSnackbar({ open: true, message: 'Order updated successfully', severity: 'success' });
        } catch (error) {
            console.error('Error updating order:', error);
            const errorMessage = error.response?.data?.message || 'Failed to update order. Please try again.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    const handleProductSelectToShip = (productId) => {
        setSelectedProductsToShip(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleProductSelectToUnship = (productId) => {
        setSelectedProductsToUnship(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleBatchShip = async () => {
        if (selectedProductsToShip.length === 0) return;

        try {
            await api.put(`/orders/${order._id}/ship-products`, {
                productIds: selectedProductsToShip
            });

            setEditedOrder(prevOrder => ({
                ...prevOrder,
                products: prevOrder.products.map(p =>
                    selectedProductsToShip.includes(p._id)
                        ? { ...p, status: 'shipped' }
                        : p
                )
            }));

            setSelectedProductsToShip([]);
            onOrderUpdate();
            setSnackbar({ open: true, message: 'Products marked as shipped', severity: 'success' });
        } catch (error) {
            console.error('Error shipping products:', error);
            setSnackbar({ open: true, message: 'Failed to ship products', severity: 'error' });
        }
    };

    const handleBatchUnship = () => {
        if (selectedProductsToUnship.length === 0) return;

        setConfirmDialog({
            open: true,
            message: `Are you sure you want to mark ${selectedProductsToUnship.length} product(s) as ordered?`,
            action: async () => {
                try {
                    await api.put(`/orders/${order._id}/unship-products`, {
                        productIds: selectedProductsToUnship
                    });

                    setEditedOrder(prevOrder => ({
                        ...prevOrder,
                        products: prevOrder.products.map(p =>
                            selectedProductsToUnship.includes(p._id)
                                ? { ...p, status: 'ordered', billed: false }
                                : p
                        )
                    }));

                    setSelectedProductsToUnship([]);
                    setSelectedProductsToUnbill([]); // Clear unbill selection since those products are now ordered
                    setConfirmDialog({ open: false, action: null, message: '' });
                    onOrderUpdate();
                    setSnackbar({ open: true, message: 'Products marked as ordered', severity: 'success' });
                } catch (error) {
                    console.error('Error unshipping products:', error);
                    setSnackbar({ open: true, message: 'Failed to update products', severity: 'error' });
                }
            }
        });
    };

    const handleProductSelectToBill = (productId) => {
        setSelectedProductsToBill(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleProductSelectToUnbill = (productId) => {
        setSelectedProductsToUnbill(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleBatchBill = async () => {
        if (selectedProductsToBill.length === 0) return;

        try {
            await api.put(`/orders/${order._id}/bill-products`, {
                productIds: selectedProductsToBill
            });

            setEditedOrder(prevOrder => ({
                ...prevOrder,
                products: prevOrder.products.map(p =>
                    selectedProductsToBill.includes(p._id)
                        ? { ...p, billed: true }
                        : p
                )
            }));

            setSelectedProductsToBill([]);
            onOrderUpdate();
            setSnackbar({ open: true, message: 'Products marked as billed', severity: 'success' });
        } catch (error) {
            console.error('Error billing products:', error);
            setSnackbar({ open: true, message: 'Failed to bill products', severity: 'error' });
        }
    };

    const handleBatchUnbill = async () => {
        if (selectedProductsToUnbill.length === 0) return;

        try {
            await api.put(`/orders/${order._id}/unbill-products`, {
                productIds: selectedProductsToUnbill
            });

            setEditedOrder(prevOrder => ({
                ...prevOrder,
                products: prevOrder.products.map(p =>
                    selectedProductsToUnbill.includes(p._id)
                        ? { ...p, billed: false }
                        : p
                )
            }));

            setSelectedProductsToUnbill([]);
            onOrderUpdate();
            setSnackbar({ open: true, message: 'Products marked as unbilled', severity: 'success' });
        } catch (error) {
            console.error('Error unbilling products:', error);
            setSnackbar({ open: true, message: 'Failed to unbill products', severity: 'error' });
        }
    };

    const handleDeleteOrder = async () => {
        try {
            if (userRole !== 'owner' && userLocation !== 'Universal') {
                const productsToDelete = originalOrder.products
                    .filter(product => product.location === userLocation)
                    .map(product => product._id);

                await api.put(`/orders/${order._id}/delete-products`, {
                    productsToDelete,
                    userLocation
                });
            } else {
                await api.delete(`/orders/${order._id}`);
            }
            setSnackbar({ open: true, message: 'Order deleted successfully', severity: 'success' });
            onOrderUpdate();
            setOpen(false);
        } catch (error) {
            console.error('Error deleting order:', error);
            setSnackbar({ open: true, message: 'Failed to delete order', severity: 'error' });
        }
    };

    const handleStatusChange = (newStatus) => {
        const message = newStatus === 'pending'
            ? 'Are you sure you want to set this order back to pending? This will mark the order as incomplete.'
            : 'Are you sure you want to mark this order as completed? This action confirms all products have been delivered.';

        setConfirmDialog({
            open: true,
            message: message,
            action: () => confirmStatusChange(newStatus)
        });
    };

    const confirmStatusChange = async (newStatus) => {
        setConfirmDialog({ open: false, action: null, message: '' });

        try {
            const endpoint = newStatus === 'pending'
                ? `/orders/pending/${order._id}`
                : `/orders/complete/${order._id}`;

            const response = await api.put(endpoint);
            setEditedOrder(response.data);
            setOriginalOrder(response.data);
            onOrderUpdate();
            setSnackbar({ open: true, message: `Order set to ${newStatus} successfully`, severity: 'success' });
        } catch (error) {
            console.error(`Error setting order to ${newStatus}:`, error);
            setSnackbar({ open: true, message: `Failed to set order to ${newStatus}`, severity: 'error' });
        }
    };

    return (
        <>
            <TableRow hover onClick={handleRowClick} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small">
                        {open ? <ArrowUpIcon /> : <ArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{order.party.firmName}</TableCell>
                <TableCell>
                    {displayedProducts.length > 0
                        ? displayedProducts.map(p => `${p.productName} (${p.quantity})`).join(", ")
                        : 'No pending products'}
                </TableCell>
                {!isMobile && <TableCell>{order.status}</TableCell>}
                {!isTablet && <TableCell>{order.createdBy?.name || 'Unknown'}</TableCell>}
                <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {editedOrder && (
                            <Box sx={{ margin: isMobile ? 1 : 2, pb: 2 }}>
                                {/* Order Header with Status Top-Right */}
                                <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            Order Details
                                        </Typography>
                                        <Chip
                                            label={editedOrder.status}
                                            size="small"
                                            color={editedOrder.status === 'completed' ? 'success' : 'primary'}
                                        />
                                    </Box>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={8}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                📅 {new Date(editedOrder.createdAt).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="body2" align={isMobile ? 'left' : 'right'}>
                                                By: {editedOrder.createdBy?.name || 'Unknown'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Party Details - Reorganized */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                                        Party Information
                                    </Typography>
                                    {isEditing ? (
                                        <Autocomplete
                                            options={partyOptions || []}
                                            loading={partyLoading}
                                            value={editedOrder.party}
                                            onChange={handlePartyChange}
                                            onInputChange={(event, value, reason) => {
                                                if (reason === 'input') {
                                                    fetchParties(value);
                                                }
                                            }}
                                            getOptionLabel={(option) => option?.firmName || ''}
                                            isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                            disabled={!canEditParty}
                                            freeSolo={false}
                                            clearOnBlur={true}
                                            selectOnFocus={true}
                                            autoHighlight={true}
                                            disablePortal
                                            filterOptions={(x) => x}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Party Name"
                                                    required
                                                    error={!!errors.party}
                                                    helperText={errors.party}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {partyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        autoComplete: 'new-password',
                                                    }}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        <strong>Firm:</strong> {editedOrder.party.firmName}
                                                    </Typography>
                                                    {editedOrder.party.firmAddress && (
                                                        <Typography variant="body2">
                                                            <strong>Address:</strong> {editedOrder.party.firmAddress}
                                                            {editedOrder.party.firmCityState && `, ${editedOrder.party.firmCityState}`}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    {editedOrder.party.contactPerson && (
                                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                            <strong>Contact:</strong> {editedOrder.party.contactPerson}
                                                        </Typography>
                                                    )}
                                                    {editedOrder.party.phoneNumber && (
                                                        <Typography variant="body2">
                                                            <strong>Phone:</strong> {editedOrder.party.phoneNumber}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    )}
                                </Box>

                                {/* Products Section */}
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                                    Products
                                </Typography>
                                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {!isEditing && userRole !== 'delivery_person' && editedOrder.status === 'pending' && (
                                                    <TableCell padding="checkbox"></TableCell>
                                                )}
                                                <TableCell><strong>Product</strong></TableCell>
                                                {!isMobile && <TableCell><strong>Qty</strong></TableCell>}
                                                {!isMobile && <TableCell><strong>Rate</strong></TableCell>}
                                                {!isMobile && <TableCell><strong>Location</strong></TableCell>}
                                                {!isMobile && <TableCell><strong>Status</strong></TableCell>}
                                                {!isEditing && !isMobile && userRole !== 'delivery_person' && <TableCell align="center" sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}><strong>Billed</strong></TableCell>}
                                                {isEditing && !isMobile && <TableCell align="center"><strong>Action</strong></TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {editedOrder.products.map((product, index) => (
                                                <TableRow key={index} sx={{
                                                    backgroundColor: product.billed ? '#e3f2fd' : (product.status === 'shipped' ? '#f0fdf4' : 'inherit'),
                                                    opacity: product.billed ? 0.85 : 1
                                                }}>
                                                    {!isEditing && userRole !== 'delivery_person' && editedOrder.status === 'pending' && (
                                                        <TableCell padding="checkbox">
                                                            {product.status === 'ordered' && canEditProduct(product) && (
                                                                <Checkbox
                                                                    checked={selectedProductsToShip.includes(product._id)}
                                                                    onChange={() => handleProductSelectToShip(product._id)}
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            )}
                                                            {product.status === 'shipped' && canEditProduct(product) && (
                                                                <Checkbox
                                                                    checked={selectedProductsToUnship.includes(product._id)}
                                                                    onChange={() => handleProductSelectToUnship(product._id)}
                                                                    color="warning"
                                                                    size="small"
                                                                />
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        {isMobile ? (
                                                            // Mobile view - all info in one cell
                                                            isEditing && product.status !== 'shipped' ? (
                                                                <Box>
                                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                                                        <Autocomplete
                                                                            options={productOptions || []}
                                                                            loading={productLoading}
                                                                            value={product.productName ? { productName: product.productName } : null}
                                                                            onChange={(event, value) => handleProductChange(index, 'product', value, event)}
                                                                            onInputChange={(event, value, reason) => {
                                                                                if (reason === 'input') {
                                                                                    fetchProducts(value);
                                                                                }
                                                                            }}
                                                                            getOptionLabel={(option) => option?.productName || ''}
                                                                            isOptionEqualToValue={(option, value) => option?.productName === value?.productName}
                                                                            disabled={product.status === 'shipped'}
                                                                            freeSolo={false}
                                                                            clearOnBlur={true}
                                                                            selectOnFocus={true}
                                                                            autoHighlight={true}
                                                                            disablePortal
                                                                            clearOnEscape={true}
                                                                            openOnFocus={false}
                                                                            clearOnFocus={true}
                                                                            filterOptions={(x) => x}
                                                                            sx={{ flexGrow: 1 }}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Product"
                                                                                    required
                                                                                    error={!!errors[`productName-${index}`]}
                                                                                    helperText={errors[`productName-${index}`]}
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        endAdornment: (
                                                                                            <>
                                                                                                {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                                {params.InputProps.endAdornment}
                                                                                            </>
                                                                                        ),
                                                                                    }}
                                                                                    inputProps={{
                                                                                        ...params.inputProps,
                                                                                        autoComplete: 'new-password',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        />
                                                                        {canEditProduct(product) && (
                                                                            <IconButton
                                                                                onClick={() => handleRemoveProduct(index)}
                                                                                size="small"
                                                                                color="error"
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                    {/* Stock Info Display for Mobile */}
                                                                    {stockInfo[index] && (
                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Stock at {product.location}: Current: <strong>{stockInfo[index].current}</strong>,
                                                                                Reserved: <strong style={{ color: '#ed6c02' }}>{stockInfo[index].reserved}</strong>,
                                                                                Available: <strong style={{ color: stockInfo[index].available > 0 ? '#2e7d32' : '#d32f2f' }}>
                                                                                    {stockInfo[index].available}
                                                                                </strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <TextField
                                                                            type="number"
                                                                            value={product.quantity}
                                                                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                                            onFocus={(e) => e.target.select()}
                                                                            label="Qty"
                                                                            required
                                                                            error={!!errors[`quantity-${index}`] && !errors[`quantity-${index}`]?.includes('Warning')}
                                                                            helperText={errors[`quantity-${index}`]}
                                                                            size="small"
                                                                            sx={{ flexGrow: 1 }}
                                                                            slotProps={{
                                                                                formHelperText: {
                                                                                    sx: {
                                                                                        color: errors[`quantity-${index}`]?.includes('Warning')
                                                                                            ? '#ed6c02'
                                                                                            : undefined
                                                                                    }
                                                                                }
                                                                            }}
                                                                        />
                                                                        <TextField
                                                                            type="number"
                                                                            value={product.rate}
                                                                            onChange={(e) => handleProductChange(index, 'rate', e.target.value)}
                                                                            onFocus={(e) => e.target.select()}
                                                                            label="Rate"
                                                                            required
                                                                            error={!!errors[`rate-${index}`]}
                                                                            helperText={errors[`rate-${index}`]}
                                                                            size="small"
                                                                            sx={{ flexGrow: 1 }}
                                                                        />
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={product.rateIncludesGST || false}
                                                                                    onChange={(e) => handleProductChange(index, 'rateIncludesGST', e.target.checked)}
                                                                                    size="small"
                                                                                />
                                                                            }
                                                                            label={<Typography variant="caption">GST</Typography>}
                                                                            sx={{ flexShrink: 0 }}
                                                                        />
                                                                        <Autocomplete
                                                                            options={STORAGE_LOCATIONS}
                                                                            value={product.location}
                                                                            onChange={(_event, newValue) => handleProductChange(index, 'location', newValue)}
                                                                            disableFreeSolo
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Location"
                                                                                    required
                                                                                    error={!!errors[`location-${index}`]}
                                                                                    helperText={errors[`location-${index}`]}
                                                                                    size="small"
                                                                                />
                                                                            )}
                                                                            sx={{ flexGrow: 1 }}
                                                                        />
                                                                    </Box>
                                                                    {/* Description field for mobile edit */}
                                                                    <TextField
                                                                        fullWidth
                                                                        value={product.description || ''}
                                                                        onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                                                                        label="Description (Optional)"
                                                                        placeholder="Add special notes..."
                                                                        size="small"
                                                                        sx={{ mt: 1 }}
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                                                    <Box sx={{ flexGrow: 1 }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                            {product.productName}
                                                                        </Typography>
                                                                        {product.description && (
                                                                            <Typography variant="caption" color="primary.main" display="block" sx={{ fontStyle: 'italic' }}>
                                                                                {product.description}
                                                                            </Typography>
                                                                        )}
                                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                                            Location: {product.location}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                                            Qty: {product.quantity} | Rate: ₹{product.rate}{!product.rateIncludesGST && ' +GST'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, flexShrink: 0 }}>
                                                                        <Chip
                                                                            label={product.status}
                                                                            size="small"
                                                                            color={product.status === 'shipped' ? 'success' : 'default'}
                                                                        />
                                                                        {product.status === 'shipped' && userRole !== 'delivery_person' && (
                                                                            <Checkbox
                                                                                checked={product.billed ? selectedProductsToUnbill.includes(product._id) : selectedProductsToBill.includes(product._id)}
                                                                                onChange={() => product.billed ? handleProductSelectToUnbill(product._id) : handleProductSelectToBill(product._id)}
                                                                                color={product.billed ? 'primary' : 'default'}
                                                                                size="small"
                                                                                icon={product.billed ? <CheckCircleIcon color="primary" /> : undefined}
                                                                                checkedIcon={product.billed ? <CheckCircleIcon color="warning" /> : undefined}
                                                                                sx={{ p: 0.5, mt: 0.5 }}
                                                                                title={product.billed ? 'Billed - click to unbill' : 'Not billed - click to bill'}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )
                                                        ) : (
                                                            // Desktop view
                                                            isEditing && product.status !== 'shipped' ? (
                                                                <Box>
                                                                    <Autocomplete
                                                                        options={productOptions || []}
                                                                        loading={productLoading}
                                                                        value={product.productName ? { productName: product.productName } : null}
                                                                        onChange={(event, value) => handleProductChange(index, 'product', value, event)}
                                                                        onInputChange={(event, value, reason) => {
                                                                            if (reason === 'input') {
                                                                                fetchProducts(value);
                                                                            }
                                                                        }}
                                                                        getOptionLabel={(option) => option?.productName || ''}
                                                                        isOptionEqualToValue={(option, value) => option?.productName === value?.productName}
                                                                        disabled={product.status === 'shipped'}
                                                                        freeSolo={false}
                                                                        clearOnBlur={true}
                                                                        selectOnFocus={true}
                                                                        autoHighlight={true}
                                                                        disablePortal
                                                                        clearOnEscape={true}
                                                                        openOnFocus={false}
                                                                        clearOnFocus={true}
                                                                        filterOptions={(x) => x}
                                                                        sx={{ minWidth: '150px' }}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Product"
                                                                                required
                                                                                error={!!errors[`productName-${index}`]}
                                                                                helperText={errors[`productName-${index}`]}
                                                                                InputProps={{
                                                                                    ...params.InputProps,
                                                                                    endAdornment: (
                                                                                        <>
                                                                                            {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                            {params.InputProps.endAdornment}
                                                                                        </>
                                                                                    ),
                                                                                }}
                                                                                inputProps={{
                                                                                    ...params.inputProps,
                                                                                    autoComplete: 'new-password',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    />
                                                                    {/* Description field for desktop edit */}
                                                                    <TextField
                                                                        fullWidth
                                                                        value={product.description || ''}
                                                                        onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                                                                        label="Description"
                                                                        placeholder="Add special notes..."
                                                                        size="small"
                                                                        sx={{ mt: 1, minWidth: '150px' }}
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <Box>
                                                                    <Typography variant="body2">{product.productName}</Typography>
                                                                    {product.description && (
                                                                        <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                                                                            {product.description}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </TableCell>
                                                    {!isMobile && (
                                                        <>
                                                            <TableCell>
                                                                {isEditing && product.status !== 'shipped' ? (
                                                                    <Box>
                                                                        <TextField
                                                                            type="number"
                                                                            value={product.quantity}
                                                                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                                            onFocus={(e) => e.target.select()}
                                                                            required
                                                                            error={!!errors[`quantity-${index}`] && !errors[`quantity-${index}`]?.includes('Warning')}
                                                                            helperText={errors[`quantity-${index}`]}
                                                                            size="small"
                                                                            sx={{ width: '100px' }}
                                                                            slotProps={{
                                                                                formHelperText: {
                                                                                    sx: {
                                                                                        color: errors[`quantity-${index}`]?.includes('Warning')
                                                                                            ? '#ed6c02'
                                                                                            : undefined
                                                                                    }
                                                                                }
                                                                            }}
                                                                        />
                                                                        {/* Stock Info Display for Desktop */}
                                                                        {stockInfo[index] && (
                                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                                                Stock: C:<strong>{stockInfo[index].current}</strong> |
                                                                                R:<strong style={{ color: '#ed6c02' }}>{stockInfo[index].reserved}</strong> |
                                                                                A:<strong style={{ color: stockInfo[index].available > 0 ? '#2e7d32' : '#d32f2f' }}>
                                                                                    {stockInfo[index].available}
                                                                                </strong>
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2">{product.quantity}</Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isEditing && product.status !== 'shipped' ? (
                                                                    <Box>
                                                                        <TextField
                                                                            type="number"
                                                                            value={product.rate}
                                                                            onChange={(e) => handleProductChange(index, 'rate', e.target.value)}
                                                                            onFocus={(e) => e.target.select()}
                                                                            required
                                                                            error={!!errors[`rate-${index}`]}
                                                                            helperText={errors[`rate-${index}`]}
                                                                            size="small"
                                                                            sx={{ width: '80px' }}
                                                                        />
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={product.rateIncludesGST || false}
                                                                                    onChange={(e) => handleProductChange(index, 'rateIncludesGST', e.target.checked)}
                                                                                    size="small"
                                                                                />
                                                                            }
                                                                            label={<Typography variant="caption">GST</Typography>}
                                                                            sx={{ mt: 0.5, ml: 0 }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="body2">
                                                                        ₹{product.rate}
                                                                        {!product.rateIncludesGST && (
                                                                            <Typography component="span" variant="caption" color="text.secondary" display="block">
                                                                                +GST
                                                                            </Typography>
                                                                        )}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {isEditing && product.status !== 'shipped' ? (
                                                                    <Autocomplete
                                                                        options={STORAGE_LOCATIONS}
                                                                        value={product.location}
                                                                        onChange={(_event, newValue) => handleProductChange(index, 'location', newValue)}
                                                                        disableFreeSolo
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Location"
                                                                                required
                                                                                error={!!errors[`location-${index}`]}
                                                                                helperText={errors[`location-${index}`]}
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                        sx={{ minWidth: '100px' }}
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body2">{product.location}</Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={product.status}
                                                                    size="small"
                                                                    color={product.status === 'shipped' ? 'success' : 'default'}
                                                                />
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    {/* Billed checkbox column - only for shipped products in non-editing mode */}
                                                    {!isEditing && !isMobile && userRole !== 'delivery_person' && (
                                                        <TableCell align="center" padding="checkbox" sx={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                                                            {product.status === 'shipped' && (
                                                                product.billed ? (
                                                                    <Checkbox
                                                                        checked={selectedProductsToUnbill.includes(product._id)}
                                                                        onChange={() => handleProductSelectToUnbill(product._id)}
                                                                        color="primary"
                                                                        size="small"
                                                                        icon={<CheckCircleIcon color="primary" />}
                                                                        checkedIcon={<CheckCircleIcon color="warning" />}
                                                                        title="Click to mark as unbilled"
                                                                    />
                                                                ) : (
                                                                    <Checkbox
                                                                        checked={selectedProductsToBill.includes(product._id)}
                                                                        onChange={() => handleProductSelectToBill(product._id)}
                                                                        color="primary"
                                                                        size="small"
                                                                        title="Click to mark as billed"
                                                                    />
                                                                )
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    {isEditing && !isMobile && (
                                                        <TableCell align="center">
                                                            {product.status !== 'shipped' && canEditProduct(product) && (
                                                                <IconButton
                                                                    onClick={() => handleRemoveProduct(index)}
                                                                    size="small"
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>

                                {/* Batch Action Buttons for Ship/Unship */}
                                {!isEditing && userRole !== 'delivery_person' && editedOrder.status === 'pending' && (
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {selectedProductsToShip.length > 0 && (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={<CheckCircleIcon />}
                                                onClick={handleBatchShip}
                                            >
                                                Mark as Shipped ({selectedProductsToShip.length})
                                            </Button>
                                        )}
                                        {selectedProductsToUnship.length > 0 && (
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                size="small"
                                                onClick={handleBatchUnship}
                                            >
                                                Mark as Ordered ({selectedProductsToUnship.length})
                                            </Button>
                                        )}
                                    </Box>
                                )}

                                {/* Batch Action Buttons for Bill/Unbill - works for both pending and completed orders */}
                                {!isEditing && userRole !== 'delivery_person' && (
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {selectedProductsToBill.length > 0 && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={handleBatchBill}
                                            >
                                                Mark as Billed ({selectedProductsToBill.length})
                                            </Button>
                                        )}
                                        {selectedProductsToUnbill.length > 0 && (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                onClick={handleBatchUnbill}
                                            >
                                                Mark as Unbilled ({selectedProductsToUnbill.length})
                                            </Button>
                                        )}
                                    </Box>
                                )}

                                {isEditing && editedOrder.status === 'pending' && (
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={handleAddProduct}
                                        sx={{ mt: 2 }}
                                        size="small"
                                        variant="outlined"
                                    >
                                        Add Product
                                    </Button>
                                )}

                                {/* Action Buttons */}
                                <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    {userRole !== 'delivery_person' && (
                                        <>
                                            {!isEditing && editedOrder.status === 'pending' && (
                                                <Button
                                                    onClick={handleEditToggle}
                                                    startIcon={<EditIcon />}
                                                    variant="outlined"
                                                    size="small"
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            {isEditing && (
                                                <>
                                                    <Button
                                                        onClick={handleSaveChanges}
                                                        startIcon={<SaveIcon />}
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        onClick={handleEditToggle}
                                                        startIcon={<CloseIcon />}
                                                        variant="outlined"
                                                        color="secondary"
                                                        size="small"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    {editedOrder.products.length === 0 && (
                                                        <Button
                                                            onClick={handleDeleteOrder}
                                                            startIcon={<DeleteIcon />}
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                        >
                                                            Delete Order
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                            {!isEditing && editedOrder.status === 'pending' && editedOrder.products.length > 0 && canEditOrder && (
                                                <Button
                                                    onClick={() => handleStatusChange('complete')}
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                >
                                                    Complete Order
                                                </Button>
                                            )}
                                            {!isEditing && editedOrder.status === 'completed' && (
                                                <Button
                                                    onClick={() => handleStatusChange('pending')}
                                                    variant="outlined"
                                                    color="warning"
                                                    size="small"
                                                >
                                                    Set to Pending
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>

            {/* Confirm Dialog */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, message: '' })}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, action: null, message: '' })}>Cancel</Button>
                    <Button onClick={confirmDialog.action} color="primary">Yes</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default OrderRow;
